<template>
    <div class="mechanic-asset-page">
        <div class="mechanic-asset-page__header">
            <div class="mechanic-asset-page__title-row">
                <b-button
                        size="sm"
                        variant="outline-secondary"
                        class="mechanic-asset-page__back"
                        @click="goBackToList"
                >
                    <i class="fas fa-arrow-left mr-1"></i>
                    Назад
                </b-button>
                <div class="mechanic-asset-page__title-block">
                    <div class="mechanic-asset-page__section">Справочник механиков</div>
                    <h4 class="mechanic-asset-page__title">{{ pageTitle }}</h4>
                    <div v-if="selectedUnit" class="mechanic-asset-page__subtitle">
                        #{{ selectedUnit.id }}
                        <span v-if="selectedUnit.catalog_name"> · {{ selectedUnit.catalog_name }}</span>
                    </div>
                </div>
            </div>
            <b-button
                    size="sm"
                    variant="outline-primary"
                    :disabled="pageRefreshing || pageLoading || cardLoading"
                    @click="refreshCard"
            >
                <b-spinner v-if="pageRefreshing" small class="mr-1"></b-spinner>
                <i v-else class="fas fa-sync-alt mr-1"></i>
                Обновить
            </b-button>
        </div>

        <b-alert
                v-if="errorMessage"
                variant="danger"
                show
                dismissible
                @dismissed="errorMessage = null"
        >
            {{ errorMessage }}
        </b-alert>

        <b-alert v-if="pageLoading || cardLoading" show variant="light" class="border">
            <b-spinner small class="mr-2"></b-spinner>
            Загрузка карточки позиции...
        </b-alert>

        <b-alert v-else-if="!selectedUnit && !errorMessage" show variant="warning">
            Позиция не найдена или недоступна.
        </b-alert>

        <MechanicAssetCard
                :unit="selectedUnit"
                :history-rows="historyRows"
                :note-rows="noteRows"
                :loading="cardLoading"
        />
    </div>
</template>

<script>
import MechanicAssetCard from './MechanicAssetCard.vue';
import {updateRecentWorkItem} from '@/utils/recentWork';

export default {
    name: `MechanicAssetPage`,
    components: {
        MechanicAssetCard
    },
    data() {
        return {
            errorMessage: null,
            pageLoading: true,
            pageRefreshing: false,
            telegramBackButtonHandler: null,
            telegramHistoryBackInProgress: false
        };
    },
    computed: {
        state() {
            return this.$store.state.mechanicAssets;
        },
        selectedUnit() {
            return this.state.selectedUnit;
        },
        historyRows() {
            return this.state.selectedHistory;
        },
        noteRows() {
            return this.state.selectedNotes;
        },
        cardLoading() {
            return this.state.cardLoading;
        },
        isTelegramWebApps() {
            return this.$store.getters.isTelegramWebApps;
        },
        unitId() {
            return Number(this.$route.params.id);
        },
        pageTitle() {
            return this.selectedUnit ? this.unitTitle(this.selectedUnit) : `Карточка позиции`;
        }
    },
    watch: {
        '$route.params.id'() {
            this.loadUnit();
        }
    },
    mounted() {
        this.setupTelegramBackNavigation();
        window.addEventListener(`focus`, this.autoRefreshOnResume);
        window.addEventListener(`pageshow`, this.handlePageShow);
        document.addEventListener(`visibilitychange`, this.handleVisibilityChange);
        this.fetchInitialData();
    },
    beforeDestroy() {
        this.teardownTelegramBackNavigation();
        window.removeEventListener(`focus`, this.autoRefreshOnResume);
        window.removeEventListener(`pageshow`, this.handlePageShow);
        document.removeEventListener(`visibilitychange`, this.handleVisibilityChange);
        this.$store.dispatch(`mechanicAssets/closeUnit`);
    },
    methods: {
        async fetchInitialData() {
            this.errorMessage = null;
            this.pageLoading = true;
            try {
                await Promise.all([
                    this.$store.dispatch(`mechanicAssets/fetchAccessSettings`),
                    this.$store.dispatch(`mechanicAssets/fetchDictionaries`),
                    this.$store.dispatch(`getObjectsInProcess`),
                    this.$store.dispatch(`getUsers`, {full: true})
                ]);
                await this.loadUnit();
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки карточки механиков`);
            } finally {
                this.pageLoading = false;
            }
        },
        async loadUnit() {
            if (!Number.isInteger(this.unitId) || this.unitId <= 0) {
                this.$store.dispatch(`mechanicAssets/closeUnit`);
                this.errorMessage = `Некорректный ID позиции.`;
                return null;
            }

            this.errorMessage = null;
            try {
                const result = await this.$store.dispatch(`mechanicAssets/openUnit`, this.unitId);
                const unit = result || this.selectedUnit;
                if (unit) {
                    updateRecentWorkItem(
                        this.$store.getters.currentUser,
                        'mechanic_asset',
                        this.unitId,
                        {
                            label: this.unitTitle(unit),
                            contextLabel: `Техника #${this.unitId}`
                        }
                    );
                }
                return unit;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки карточки механиков`);
                return null;
            }
        },
        async refreshCard() {
            if (this.pageRefreshing || this.cardLoading) {
                return;
            }
            this.pageRefreshing = true;
            this.errorMessage = null;
            try {
                await Promise.all([
                    this.$store.dispatch(`mechanicAssets/fetchDictionaries`),
                    this.$store.dispatch(`getObjectsInProcess`),
                    this.$store.dispatch(`getUsers`, {full: true})
                ]);
                if (this.selectedUnit && Number(this.selectedUnit.id) === this.unitId) {
                    await this.$store.dispatch(`mechanicAssets/refreshSelectedUnit`);
                } else {
                    await this.loadUnit();
                }
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка обновления карточки механиков`);
            } finally {
                this.pageRefreshing = false;
            }
        },
        async autoRefreshOnResume() {
            if ((typeof document !== `undefined` && document.hidden) ||
                this.pageRefreshing ||
                this.cardLoading) {
                return;
            }
            await this.refreshCard();
        },
        handleVisibilityChange() {
            if (typeof document === `undefined` || !document.hidden) {
                this.autoRefreshOnResume();
            }
        },
        handlePageShow() {
            this.autoRefreshOnResume();
        },
        goBackToList(options = {}) {
            const unitId = this.selectedUnit && this.selectedUnit.id ? this.selectedUnit.id : this.unitId;
            const route = {
                name: `MechanicAssets`,
                query: unitId ? {highlight: String(unitId)} : {}
            };
            const method = options && options.replace ? `replace` : `push`;
            return this.$router[method](route).catch(() => {});
        },
        getTelegramBackButton() {
            return window.Telegram
                && window.Telegram.WebApp
                && window.Telegram.WebApp.BackButton
                ? window.Telegram.WebApp.BackButton
                : null;
        },
        setupTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === `undefined`) {
                return;
            }

            this.telegramBackButtonHandler = () => {
                if (
                    this.hasMechanicAssetOverlayOpen()
                    && window.history
                    && typeof window.history.back === `function`
                ) {
                    window.history.back();
                    return;
                }
                this.goBackToList({replace: true});
            };

            const backButton = this.getTelegramBackButton();
            if (backButton) {
                if (typeof backButton.onClick === `function`) {
                    backButton.onClick(this.telegramBackButtonHandler);
                }
                if (typeof backButton.show === `function`) {
                    backButton.show();
                }
            }

            window.addEventListener(`popstate`, this.handleTelegramHistoryBack);
            this.pushTelegramHistorySentinel();
        },
        teardownTelegramBackNavigation() {
            if (!this.isTelegramWebApps || typeof window === `undefined`) {
                return;
            }

            const backButton = this.getTelegramBackButton();
            if (
                backButton &&
                this.telegramBackButtonHandler &&
                typeof backButton.offClick === `function`
            ) {
                backButton.offClick(this.telegramBackButtonHandler);
            }
            if (backButton && typeof backButton.hide === `function`) {
                backButton.hide();
            }

            window.removeEventListener(`popstate`, this.handleTelegramHistoryBack);
            this.telegramBackButtonHandler = null;
        },
        pushTelegramHistorySentinel() {
            try {
                const state = window.history.state || {};
                if (state && state.mechanicAssetCardTelegramBack) {
                    return;
                }
                window.history.pushState(
                    Object.assign({}, state, {
                        mechanicAssetCardTelegramBack: true,
                        mechanicAssetCardId: String(this.$route.params.id || ``)
                    }),
                    document.title,
                    window.location.href
                );
            } catch (error) {
                console.error(`Не удалось подготовить Telegram back navigation карточки механиков:`, error);
            }
        },
        handleTelegramHistoryBack() {
            const suppressBack = typeof window !== `undefined`
                && (window.__mechanicAssetCardSuppressTelegramBack || window.__appFileViewerSuppressHistoryBack);
            if (suppressBack) {
                window.__mechanicAssetCardSuppressTelegramBack = false;
                window.__appFileViewerSuppressHistoryBack = false;
                return;
            }

            if (
                !this.isTelegramWebApps ||
                this.telegramHistoryBackInProgress ||
                this.$route.name !== `MechanicAssetCard` ||
                this.hasMechanicAssetOverlayOpen()
            ) {
                return;
            }

            this.telegramHistoryBackInProgress = true;
            this.goBackToList({replace: true})
                .finally(() => {
                    this.telegramHistoryBackInProgress = false;
                });
        },
        hasMechanicAssetOverlayOpen() {
            return typeof document !== `undefined`
                && (
                    document.querySelector(`.modal.show`)
                    || document.querySelector(`.blueimp-gallery-display`)
                );
        },
        unitTitle(unit) {
            if (!unit) {
                return `Карточка позиции`;
            }
            return unit.display_name || unit.catalog_name || `Позиция #${unit.id}`;
        },
        errorText(err, fallback) {
            return err && err.response && err.response.data && err.response.data.message
                ? err.response.data.message
                : fallback;
        }
    }
};
</script>

<style scoped>
.mechanic-asset-page {
    padding: 12px;
}

.mechanic-asset-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.mechanic-asset-page__title-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
}

.mechanic-asset-page__back {
    flex-shrink: 0;
}

.mechanic-asset-page__title-block {
    min-width: 0;
}

.mechanic-asset-page__section {
    color: #6c757d;
    font-size: 12px;
    line-height: 1.2;
}

.mechanic-asset-page__title {
    margin: 0;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.mechanic-asset-page__subtitle {
    color: #6c757d;
    font-size: 13px;
    line-height: 1.3;
    overflow-wrap: anywhere;
}

@media (max-width: 767.98px) {
    .mechanic-asset-page {
        padding: 8px;
    }

    .mechanic-asset-page__header {
        flex-direction: column;
        align-items: stretch;
    }

    .mechanic-asset-page__title-row {
        align-items: center;
    }
}
</style>
